import { Box, Image, RadioButtonGroup, Text, SelectMultiple } from 'grommet';
import { StyledTooltip } from '../../../../../components/common/StyledTooltip';
import infoIcon from '../../../../../assets/BoundaryTool/info-icon.svg';
import React, { useState, useEffect, useRef } from 'react';
import { t } from 'i18next';
import { useAppSelector } from '../../../../../redux';
import { FormInputProps } from '../FieldItem';
import { useFlags } from 'launchdarkly-react-client-sdk';

const commonStyle: React.CSSProperties = { fontWeight: '400', fontSize: '16px', lineHeight: '24px', border: 'none' };
const titleStyle: React.CSSProperties = { ...commonStyle, color: '#282828', textAlign: 'start' };

export const PracticeTypeInput: React.FC<FormInputProps> = ({ field, saveField, options, country }) => {
  const { enableDominantPlantSelect } = useFlags();

  const isPastureType = field.properties?.['practiceType'] === 'PASTURE';
  const hasICL = field.properties?.practices.indexOf('ICL') > -1;

  const handleChange = (event: any) => {
    const practices = country === 'br' && hasICL ? ['ICL'] : [];
    saveField({ ...field, properties: { ...field.properties, practiceType: event.target.value, practices } });
  };

  return (
    <Box gap={'4px'}>
      <Box direction={'row'}>
        <Text style={titleStyle}>{t('fieldFlow.fieldItem.practiceType.title')}</Text>
        <TogglePracticeTooltip country={country} />
      </Box>
      <RadioButtonGroup
        direction={'row'}
        name={'practiceType'}
        data-cy={`field-practiceType-${field.properties?.['name']}`}
        value={field.properties?.['practiceType']}
        onChange={handleChange}
        options={options}
      />
      {isPastureType && enableDominantPlantSelect && <DominantPlantSelect field={field} saveField={saveField} />}
    </Box>
  );
};

interface PlantSpecie {
  id: string;
  name: string;
}

const DominantPlantSelect = ({ field, saveField }: any) => {
  const plantSpecies: PlantSpecie[] = useAppSelector((state) => state.dataCollection.forageDominantPlantSpecies);
  const options: PlantSpecie[] = (plantSpecies?.map(({ id, name }) => ({ id, name })) || []).sort((a, b) => a.name.localeCompare(b.name));

  const [selectedPlants, setSelectedPlants] = useState<PlantSpecie[]>(field.properties?.dominantPlants || []);
  const [filteredOptions, setFilteredOptions] = useState<PlantSpecie[]>(options);

  const [isOpen, setIsOpen] = useState(false);
  const [scrolling, setScrolling] = useState<boolean>(false);
  const [mouseInside, setMouseInside] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [plantSpecies]);

  const handleSearch = (text: string) => {
    const filtered = options.filter(({ name }: PlantSpecie) => name.toLowerCase().indexOf(text.toLowerCase()) >= 0);
    setFilteredOptions(filtered);
  };

  const handleChange = (event: { value: PlantSpecie[] }) => {
    setSelectedPlants(event.value);
    saveField({ ...field, properties: { ...field.properties, dominantPlants: event.value ?? [] } });
  };

  const handleOpen = () => {
    setFilteredOptions(options);
    setIsOpen(true);

    setTimeout(() => {
      const dropdown = document.querySelector('[role="listbox"]');
      if (!dropdown) return;

      dropdown.addEventListener('mouseenter', () => setMouseInside(true));
      dropdown.addEventListener('mouseleave', () => setMouseInside(false));

      const cleanup = () => {
        dropdown.removeEventListener('mouseenter', () => setMouseInside(true));
        dropdown.removeEventListener('mouseleave', () => setMouseInside(false));
        document.removeEventListener('click', cleanup);
      };

      document.addEventListener('click', cleanup);
    }, 200);
  };

  const handleClose = () => {
    setIsOpen(false);
    setMouseInside(false);
    setScrolling(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    let timeout: NodeJS.Timeout;
    let rafId: number;
    let dropdown: Element | null = null;

    const onScroll = (e: Event) => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        if (!dropdown) {
          dropdown = document.querySelector('[role="listbox"]');
        }

        if (dropdown && dropdown.contains(e.target as Node)) {
          rafId = 0;
          return;
        }

        setScrolling(true);
        clearTimeout(timeout);
        timeout = setTimeout(() => setScrolling(false), 150);
        rafId = 0;
      });
    };

    document.addEventListener('scroll', onScroll, true);

    return () => {
      document.removeEventListener('scroll', onScroll, true);
      clearTimeout(timeout);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (scrolling && !mouseInside && isOpen) handleClose();
  }, [scrolling, mouseInside, isOpen]);

  return (
    <Box ref={selectRef} width={'48%'} gap={'4px'} margin={{ top: '8px' }}>
      <Text style={titleStyle}>{t('fieldFlow.fieldItem.dominantPlant.title')}</Text>

      <SelectMultiple
        style={{ ...commonStyle, letterSpacing: '0.07em', padding: '4px 8px', height: '32px' }}
        placeholder='Select'
        valueKey='id'
        labelKey='name'
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        onSearch={handleSearch}
        onChange={handleChange}
        value={selectedPlants}
        options={filteredOptions}
        dropTarget={selectRef.current ?? undefined}
        data-cy={'dominant-plant-select'}
        dropProps={{
          align: { top: 'bottom', left: 'left' },
          style: { minWidth: '16.4vw', maxWidth: '16.4vw', overflowY: 'auto', height: '33vh' },
          responsive: false,
        }}
      />
    </Box>
  );
};

const TogglePracticeTooltip = ({ country }: any) => {
  if (country !== 'br') return <></>;
  const filter = 'brightness(0) saturate(100%) invert(15%) sepia(0%) saturate(0%) hue-rotate(179deg) brightness(99%) contrast(98%)';

  return (
    <StyledTooltip label={t(`fieldFlow.fieldItem.practiceType.tooltip`)} showToolTip dropOptions='top'>
      <Image style={{ height: '24px', width: '24px', filter }} src={infoIcon} />
    </StyledTooltip>
  );
};
